import { browserHistory } from 'browserHistory';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import analyticsWrapper from '../../utils/analyticsWrapper';
import { commands, CommandType } from './commandsList';
import { calcMostSimilarSearchName } from './findMostSimilar';
import './CommandPallet.scss';

type BoxItemProps = {
  highlight: boolean;
  command: CommandType;
  handleNavigateClick: (command: CommandType) => void;
};

const BoxItem = ({
  highlight,
  command,
  handleNavigateClick,
}: BoxItemProps): JSX.Element => (
  <div className="box-item">
    <button
      className={clsx('box-item__button', highlight && 'highlight')}
      onClick={() => {
        analyticsWrapper.event({
          category: 'commandPalette',
          action: `click on action: ${command.path}`,
          label: 'click',
        });
        handleNavigateClick(command);
      }}
    >
      <span style={{ marginRight: '16px' }} role="img">
        {command.type === 'link' ? '🔗' : '🔍'}{' '}
      </span>
      {command.name}
    </button>
  </div>
);

const CommandPalletModal: FC<{ closePallet: () => void }> = ({
  closePallet,
}) => {
  const [value, setValue] = useState<string>('');
  const [currentCommand, setCurrentCommand] = useState<CommandType | null>(
    null
  );
  const [highlightedCommand, setHighlightedCommand] = useState<number>(-1);
  const [filteredCommands, setFilteredCommands] = useState<CommandType[]>([]);
  const inputRef = useRef<HTMLInputElement>();

  const closeCommand = useCallback(() => {
    setHighlightedCommand(-1);
    setFilteredCommands([]);
    setCurrentCommand(null);
    setValue('');
    closePallet();
  }, [closePallet]);

  const handleNavigateClick = useCallback(
    (command: CommandType) => {
      if (!command) {
        closeCommand();
        return;
      }

      setCurrentCommand(command);
      setValue(command.name);
      inputRef.current.focus();

      const goToLink = getCommandBasedOnType(value, command);
      if (goToLink) {
        if (goToLink.includes('1881')) {
          const search1881Term = value
            .split(' ')
            .filter((word, index) => index !== 0)
            .toString()
            .replace(',', ' ');
          window.open(`https://www.1881.no/?query=${search1881Term}`, '_blank');
          closeCommand();
        } else {
          browserHistory.push(goToLink);
          closeCommand();
        }
      }
    },
    [closeCommand, value]
  );

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        event.preventDefault();
        analyticsWrapper.event({
          category: 'commandPalette',
          action: `exit command pallet`,
          label: 'press escape',
        });
        closeCommand();
      }

      if (event.keyCode === 13) {
        event.preventDefault();
        analyticsWrapper.event({
          category: 'commandPalette',
          action:
            filteredCommands[highlightedCommand] &&
            filteredCommands[highlightedCommand].path
              ? `press enter on action: ${
                  (currentCommand && currentCommand.path) ||
                  filteredCommands[highlightedCommand].path
                }`
              : 'custom action',
          label: 'press enter',
        });

        handleNavigateClick(
          currentCommand || filteredCommands[highlightedCommand]
        );
      }

      if (event.keyCode === 38) {
        event.preventDefault();
        if (highlightedCommand !== 0) {
          setHighlightedCommand(cv => cv - 1);
        }
      }
      if (event.keyCode === 40) {
        event.preventDefault();
        if (highlightedCommand !== filteredCommands.length - 1) {
          setHighlightedCommand(cv => cv + 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [
    handleNavigateClick,
    filteredCommands,
    highlightedCommand,
    closeCommand,
    currentCommand,
  ]);

  useEffect(() => {
    inputRef.current.focus();
    if (filteredCommands.length > 0) {
      setHighlightedCommand(0);
    }
  }, [filteredCommands.length, setHighlightedCommand]);

  const handleSearchValueChange = (currentValue: string) => {
    setValue(currentValue);
    setCurrentCommand(null);
    const searchAsArray = currentValue.split(' ');
    const searchWord: string = searchAsArray[0].toLocaleLowerCase();

    if (searchAsArray.length >= 2) {
      const searchFor: string = searchAsArray[1].toLocaleLowerCase();
      if (searchFor.length) {
        setFilteredCommands(
          commands
            .filter(command =>
              command.name.toLocaleLowerCase().includes(searchWord)
            )
            .filter(comand => comand.type === 'advancedLink')
        );
      }
    } else {
      setFilteredCommands(
        commands
          .filter(command =>
            command.name.toLocaleLowerCase().includes(searchWord)
          )
          .filter(comand => comand.type === 'link')
      );
    }
  };

  return (
    <div
      className="command-pallet"
      onClick={ev => {
        if (
          (ev.target as HTMLDivElement).closest('.command-pallet__inner') !==
          null
        ) {
          // Don't close modal if the click is inside Inner
          return;
        }
        closeCommand();
      }}
    >
      <div className={clsx('command-pallet__inner', value && 'value')}>
        <input
          type="text"
          ref={inputRef}
          value={value}
          onChange={e => handleSearchValueChange(e.target.value)}
          className="command-pallet__input"
        />
        <div className={clsx('command-pallet__list', value && 'value')}>
          {currentCommand ? (
            <BoxItem
              highlight
              command={
                calcMostSimilarSearchName(value, commands).mostSimilarCommand
              }
              handleNavigateClick={handleNavigateClick}
            />
          ) : filteredCommands.length > 0 ? (
            filteredCommands.map((command, index) => (
              <BoxItem
                key={index}
                highlight={highlightedCommand === index}
                command={command}
                handleNavigateClick={handleNavigateClick}
              />
            ))
          ) : (
            <BoxItem
              highlight
              command={
                calcMostSimilarSearchName(value, commands).mostSimilarCommand
              }
              handleNavigateClick={handleNavigateClick}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export function CommandPallet(): JSX.Element {
  const [showPallet, setShowPallet] = useState<boolean>(false);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.metaKey && event.keyCode === 75) {
        analyticsWrapper.event({
          category: 'commandPalette',
          action: `open command pallet`,
          label: 'cmd+k',
        });
        setShowPallet(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  if (showPallet) {
    return <CommandPalletModal closePallet={() => setShowPallet(false)} />;
  }
  return null;
}

export function getCommandBasedOnType(
  value: string,
  command: CommandType
): string | null {
  if (command) {
    if (command.type === 'advancedLink') {
      const subValue = value.split(' ')[1];

      if (subValue) {
        return `${command.path.split('/:')[0]}/${subValue}`;
      }
    } else if (command.type === 'link') {
      return command.path;
    }
  }
  return null;
}
